import { type CSSProperties, type ReactNode, type Ref, useId } from 'react';
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import classNames from 'classnames';

import BasicCheck from '@/assets/icons/basic_check.svg?component';
import { useFormFieldControl } from '@/components/atoms/FormField/FormFieldContext';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import styles from './Checkbox.module.scss';

export interface CheckboxProps
    extends Omit<RadixCheckbox.CheckboxProps, 'checked' | 'onCheckedChange' | 'asChild' | 'color'> {
    /** `'indeterminate'` shows the mixed state, e.g. for a partly selected group. */
    checked?: RadixCheckbox.CheckedState;
    onCheckedChange?: (checked: boolean) => void;
    /** Rendered as a `<label>` tied to the box. Leave it out when a wrapping label names it. */
    label?: ReactNode;
    /** A series colour for chart legends: the box shows it instead of the action colour. */
    color?: string;
    /** Class for the box. */
    className?: string;
    /** Class for the row holding box and label; only used with `label`. */
    rowClassName?: string;
    labelClassName?: string;
    ref?: Ref<HTMLButtonElement>;
}

type CheckboxStyle = CSSProperties & { '--checkbox-color'?: string };

/**
 * The portal's checkbox. A Radix button with `role="checkbox"`: Space toggles it, and inside
 * a `<form>` it submits `name`/`value` through a hidden input like a native one.
 */
export default function Checkbox(props: CheckboxProps) {
    const {
        ref,
        checked,
        onCheckedChange,
        label,
        color,
        className,
        rowClassName,
        labelClassName,
        id,
        style,
        'aria-describedby': ariaDescribedBy,
        ...checkboxProps
    } = props;

    // useId's colons are valid in an id but not in a CSS selector, and tests select by id.
    const generatedId = useId().replace(/:/g, '');
    const accessible = useFormFieldControl({ id, 'aria-describedby': ariaDescribedBy });
    const checkboxId = accessible.id ?? (label === undefined ? undefined : generatedId);

    const boxStyle: CheckboxStyle | undefined = color
        ? { ...style, '--checkbox-color': color }
        : style;

    const handleCheckedChange = (checkedState: RadixCheckbox.CheckedState): void => {
        onCheckedChange?.(checkedState === true);
    };

    const box = (
        <RadixCheckbox.Root
            ref={ref}
            {...checkboxProps}
            id={checkboxId}
            aria-describedby={accessible['aria-describedby']}
            className={classNames(styles.root, color && styles.colored, className)}
            style={boxStyle}
            checked={checked}
            onCheckedChange={handleCheckedChange}>
            <RadixCheckbox.Indicator className={styles.indicator}>
                {checked === 'indeterminate' ? (
                    <span className={styles.indeterminateMark} />
                ) : (
                    <SvgIcon icon={BasicCheck} size="small" />
                )}
            </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>
    );

    if (label === undefined) {
        return box;
    }

    return (
        <span className={classNames(styles.row, rowClassName)}>
            {box}
            <label
                htmlFor={checkboxId}
                className={classNames(
                    styles.label,
                    checkboxProps.disabled && styles.labelDisabled,
                    labelClassName
                )}>
                {label}
            </label>
        </span>
    );
}
